import type React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import house from "../assets/images/house.jpg";
import img3 from "../assets/gallery/gallery_3.jpeg";
import img7 from "../assets/gallery/gallery_7.jpeg";
import { Divider, Fleuron } from "./Ornament";
import { easeManor } from "../constants/constants";
import { trackEvent } from "../lib/analytics";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.18, delayChildren: 0.2 } },
};

const rise = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 1, ease: easeManor } },
};

const Home: React.FC = () => {
  return (
    <>
      <section className="relative min-h-[92vh] flex items-center justify-center px-6 pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={house}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: "sepia(0.18) saturate(0.92) brightness(0.92)" }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(55% 75% at 50% 42%, oklch(0.965 0.012 82 / 0.82), oklch(0.965 0.012 82 / 0.97))",
            }}
          />
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="relative max-w-3xl text-center"
        >
          <motion.p
            variants={rise}
            className="font-smallcaps uppercase text-[color:var(--gilt-deep)]"
            style={{
              letterSpacing: "0.28em",
              fontSize: "clamp(0.95rem, 1.4vw, 1.125rem)",
              fontWeight: 600,
            }}
          >
            Est. MCMXXVI &nbsp;·&nbsp; Beaumont, Texas
          </motion.p>

          <motion.h1
            variants={rise}
            className="mt-2 font-wordmark leading-[0.9]"
            style={{
              color: "var(--claret-deep)",
              fontSize: "clamp(4.5rem, 14vw, 11rem)",
              textShadow:
                "0 1px 0 oklch(0.96 0.015 82), 0 2px 24px oklch(0.28 0.11 20 / 0.12)",
            }}
          >
            Moyle Manor
          </motion.h1>

          <motion.div variants={rise} className="mt-5 flex justify-center">
            <span
              className="u-script"
              style={{ fontSize: "clamp(2.4rem, 6vw, 3.8rem)" }}
            >
              a high tea affair
            </span>
          </motion.div>

          <motion.div variants={rise} className="mt-10 mx-auto max-w-xl">
            <Divider />
            <p
              className="mt-7 text-lg leading-relaxed"
              style={{ color: "var(--ink)", fontWeight: 500 }}
            >
              A quiet refuge for thoughtful moments, timeless gatherings, and
              elevated taste — in a home where hospitality has been kept since
              1926.
            </p>
            <Divider className="mt-7" />
          </motion.div>

          <motion.div
            variants={rise}
            className="mt-10 flex flex-wrap items-center justify-center gap-4"
          >
            <Link
              to="/contact"
              className="btn-seal"
              onClick={() =>
                trackEvent("home_reserve_clicked", { placement: "hero" })
              }
            >
              Request a Table
              <Fleuron className="w-7 h-2 text-[color:var(--parchment)]" />
            </Link>
            <Link to="/about" className="btn-ghost">
              Our Story
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24 grid gap-14 md:grid-cols-[1.1fr_1fr] items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1, ease: easeManor }}
        >
          <p className="u-label">The Service</p>
          <h2 className="mt-4 font-display text-5xl md:text-6xl">
            Afternoons kept by <em>candlelight</em> and china.
          </h2>
          <p className="mt-6 text-lg text-[color:var(--ink-soft)] leading-relaxed u-dropcap">
            Our high tea is poured into heirloom porcelain, on linen pressed the
            night before, in rooms that have welcomed Beaumont's guests for
            nearly a century. Scones warm, sandwiches tiered, conversation
            uninterrupted — the hours unfold as they once did in 1926.
          </p>
          <div className="mt-8">
            <Link to="/gallery" className="btn-ghost">
              Step Inside
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: easeManor }}
          className="relative"
        >
          <div className="frame-gilt rotate-[-1.2deg]">
            <img
              src={img3}
              alt="Tea service at Moyle Manor"
              className="w-full h-[420px] object-cover"
            />
          </div>
          <div className="absolute -bottom-10 -right-6 md:-right-14 w-48 md:w-56 frame-gilt rotate-[5deg]">
            <img
              src={img7}
              alt="Detail from Moyle Manor"
              className="w-full h-40 md:h-52 object-cover"
            />
          </div>
        </motion.div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <Divider />
        <h3 className="mt-8 font-display text-4xl md:text-5xl">
          By invitation, by <em>reservation</em>.
        </h3>
        <p className="mt-6 text-lg text-[color:var(--ink-soft)] mx-auto max-w-2xl leading-relaxed">
          To keep the rooms intimate and the service unhurried, seatings are
          held exclusively by arrangement. A short call is all that is asked.
        </p>
        <div className="mt-10">
          <Link
            to="/contact"
            className="btn-seal"
            onClick={() =>
              trackEvent("home_reserve_clicked", { placement: "footer_band" })
            }
          >
            Begin a Reservation
          </Link>
        </div>
        <Divider className="mt-14" />
      </section>
    </>
  );
};

export default Home;
